/**
 * 编写目的是为了实现草图中的"等距实体"功能
 * 
 * 该文件唯一功能: 输入草图实体与距离, 输出偏移后的草图实体（SketchXxxObject 实例）
 * 
 * 注意：本文件仅负责偏移计算并直接产出最终草图实体对象，
 * 预览渲染用的 Three.js Line 生成已独立到 SketchIsometricEntitiesPreview 中。
 */

import { Vector2, Vector3 } from 'three';

import {
  SketchLineObject,
  SketchEllipseObject,
  SketchArcObject,
  SketchSplineObject,
  SketchStyleSplineObject,
  SketchCircleObject,
  SketchParabolaObject,
  SketchPointObject,
  SketchPointKind,
  SketchBezierCurveKind,
  SketchBezierCurveClosureKind,
  SketchEllipseControlPointFactory,
  type SketchEllipseDefinition,
} from '../../mesh';
import {
  PointsMaterialFactory,
  LineMaterialFactory,
  SketchEdgeBaseStyle,
  LineMaterialState,
} from '../../material';
import type { ReferencePlane } from '../../reference';
import { SplineOffsetGeometryService } from './SplineOffsetGeometryService';
import type { SplineOffsetEndpointTangents, SplineOffsetGeometryResult } from './SplineOffsetGeometryService';

class SketchEntityEquidistantOffset {

  /**
   * 抛物线等距输出的样条控制点数量（固定值）。
   *
   * 说明：抛物线没有控制点概念（参数方程由顶点与焦点决定），
   * 法向偏移输出样条时按固定数量均匀选取控制点，
   * 与源曲线采样密度解耦，保证联动重算结果稳定。
   */
  private static readonly PARABOLA_OFFSET_SPLINE_CONTROL_POINT_COUNT = 8;

  /**
   * 线段偏移计算。
   *
   * 说明：沿线段法向平移起止端点，直接构造偏移后的 SketchLineObject。
   * 
   * @param lineObject 源线段对象。
   * @param distance 偏移距离（单位：毫米）。
   * @param isReverse false=正向（顺时针90°），true=反向（逆时针90°）。
   * @param referencePlane 参考平面。
   * @returns 偏移后的 SketchLineObject 实例。
   */
  static isometricSketchLineObject(
    lineObject: SketchLineObject,
    distance: number,
    isReverse: boolean,
    referencePlane: ReferencePlane
  ): SketchLineObject {
    // 1. 计算方向
    const startPoint = lineObject.startPoint.position;
    const endPoint = lineObject.endPoint.position;
    const dir = endPoint.clone().sub(startPoint).normalize(); // 线段方向（在平面内）
    const n = referencePlane.normal.clone().normalize();     // 平面法线

    // 从两侧法线中, 选取顺时针法线, 作为默认方向, 再根据 isReverse 的值确定最终方向
    let normalDirection: Vector3;
    if (isReverse === false) {
      // 默认方向（正向）：顺时针90° → dir × n
      normalDirection = new Vector3().crossVectors(dir, n).normalize();
    } else {
      // 反向：逆时针90° → n × dir
      normalDirection = new Vector3().crossVectors(n, dir).normalize();
    }

    // 2. 计算偏移之后的结果
    // WebGL/WebGPU坐标系中一个长度单位对应现实中的一米, 这里要单位转换
    const offsetMeters = distance * 0.001;
    const delta = normalDirection.clone().multiplyScalar(offsetMeters);

    const newStart = startPoint.clone().add(delta);
    const newEnd = endPoint.clone().add(delta);

    // 3. 创建控制点并构造偏移后的 SketchLineObject
    const startControlPoint = new SketchPointObject({
      position: newStart,
      pointKind: SketchPointKind.ControlPoint,
      material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
      referencePlane,
      isAuxiliaryObject: false,
    });
    const endControlPoint = new SketchPointObject({
      position: newEnd,
      pointKind: SketchPointKind.ControlPoint,
      material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
      referencePlane,
      isAuxiliaryObject: false,
    });

    return new SketchLineObject({
      referencePlane,
      isInfiniteLength: lineObject.isInfiniteLength,
      isDrawingPreviewLine: false,
      isAuxiliaryObject: false,
      startPoint: startControlPoint,
      endPoint: endControlPoint,
      material: LineMaterialFactory.getLineMaterialByBaseStyle(
        SketchEdgeBaseStyle.Basic,
        LineMaterialState.Default,
      ),
    });
  }

  /**
   * 圆弧偏移计算。
   * 
   * 说明：使用射线法自动判断默认偏移方向——
   * ① 计算起点→终点的弦方向，顺时针旋转90°得到方向A；
   * ② 从弦中点（起点→终点连线的中心点）沿方向A发射射线，求解与圆弧的交点数量；
   * ③ 交点=0（方向A指向外侧）→ 内侧偏移，减小半径；
   *    交点=1（方向A指向内侧）→ 外侧偏移，增大半径；
   * ④ isReverse=true 时，将上述默认方向反转。
   * 
   * @param arcObject 源圆弧对象。
   * @param distance 偏移距离（单位：毫米）。
   * @param isReverse false=使用射线法算出的默认方向；true=将默认方向反转。
   * @param referencePlane 参考平面。
   * @returns 偏移后的 SketchArcObject 实例；半径≤0 时返回 null。
   */
  static isometricSketchArcObject(
    arcObject: SketchArcObject,
    distance: number,
    isReverse: boolean,
    referencePlane: ReferencePlane
  ): SketchArcObject | null {
    // 1. 获取弧的定义：圆心、半径、起始角、终止角、是否顺时针、采样段数
    const definition = arcObject.getArcDefinition();
    const { centerWorld, radius, startAngle, endAngle, clockwise, segments = 64 } = definition;

    // 单位转换：毫米 → 米
    const offsetDistance = distance * 0.001;

    // ═══════════════════════════════════════════════════════════
    // 2. 射线法判断偏移方向
    // ═══════════════════════════════════════════════════════════

    // 2.1 将圆心投影到参考平面局部2D坐标
    const localCenter = referencePlane.worldToLocal(centerWorld.clone());

    // 2.2 在局部2D中计算起点和终点位置（基于原始半径）
    const localStart = new Vector2(
      localCenter.x + radius * Math.cos(startAngle),
      localCenter.y + radius * Math.sin(startAngle),
    );
    const localEnd = new Vector2(
      localCenter.x + radius * Math.cos(endAngle),
      localCenter.y + radius * Math.sin(endAngle),
    );

    // 2.3 计算起点→终点的弦方向
    const chordDir = new Vector2().subVectors(localEnd, localStart);
    const chordLength = chordDir.length();

    // 弦长为零（完整圆或起点/终点重合）：无法判断方向，默认外侧偏移
    let isOutward: boolean;
    if (chordLength < 1e-12) {
      isOutward = true; // 默认外侧偏移
    } else {
      chordDir.normalize();

      // 2.4 顺时针旋转90°得到方向A：(x, y) → (y, -x)
      // 数学原理：二维旋转矩阵 R_{-90°} = [[0, 1], [-1, 0]]
      const clockwiseDir = new Vector2(chordDir.y, -chordDir.x);

      // 2.5 射线法：从弦中点沿方向A发射射线，求解与圆的交点
      // 射线参数方程：P = O + t × clockwiseDir（t ≥ 0），O 为弦中点
      // 圆方程：|P - localCenter|² = radius²
      // 弦的两个端点都在圆上，弦中点必在圆内（|m| < R，m = O - C），
      // 代入得：t² + 2×(m·D)×t + (|m|² - R²) = 0（|D|=1）
      // 判别式恒正，且两根一正一负（常数项 < 0），正根即射线穿出点：
      // t = -(m·D) + √[(m·D)² - (|m|² - R²)]
      const midPoint = new Vector2().addVectors(localStart, localEnd).multiplyScalar(0.5);
      const m = new Vector2().subVectors(midPoint, localCenter); // 圆心→弦中点
      const mDotD = m.dot(clockwiseDir); // 弦中点向量与方向A的点积
      const mLengthSqMinusRSq = m.dot(m) - radius * radius; // 恒 < 0（弦中点在圆内）
      // 正根（射线与完整圆的穿出点参数）
      const tIntersect = -mDotD + Math.sqrt(Math.max(0, mDotD * mDotD - mLengthSqMinusRSq));

      let intersectCount = 0;

      // 计算穿出点的参数角度
      const intersectPoint = midPoint.clone().addScaledVector(clockwiseDir, tIntersect);
      const dx = intersectPoint.x - localCenter.x;
      const dy = intersectPoint.y - localCenter.y;
      const intersectAngle = Math.atan2(dy, dx);

      // 2.6 判断该交点角度是否在圆弧的 [startAngle, endAngle] 范围内
      // 角度归一化到 [0, 2π) 的辅助计算（内联实现）
      const TWO_PI = 2 * Math.PI;
      const normAngle = ((intersectAngle % TWO_PI) + TWO_PI) % TWO_PI;
      const normStart = ((startAngle % TWO_PI) + TWO_PI) % TWO_PI;
      const normEnd = ((endAngle % TWO_PI) + TWO_PI) % TWO_PI;

      // 完整圆弧（起点≈终点）：角度始终在范围内
      const isFullArc = Math.abs(normStart - normEnd) < 1e-9;

      let angleInRange: boolean;
      if (isFullArc) {
        angleInRange = true;
      } else if (clockwise) {
        // 顺时针：角度从 startAngle 减小到 endAngle
        if (normStart >= normEnd) {
          // 正常顺时针，不跨越0，例如 300°→60°（从大到小）
          angleInRange = normAngle <= normStart && normAngle >= normEnd;
        } else {
          // 跨越 0/2π，例如 60°→300°（顺时针经过0）
          angleInRange = normAngle <= normStart || normAngle >= normEnd;
        }
      } else {
        // 逆时针：角度从 startAngle 增大到 endAngle
        if (normStart <= normEnd) {
          // 正常逆时针，不跨越0，例如 60°→300°（从小到大）
          angleInRange = normAngle >= normStart && normAngle <= normEnd;
        } else {
          // 跨越 0/2π，例如 300°→60°（逆时针经过0）
          angleInRange = normAngle >= normStart || normAngle <= normEnd;
        }
      }

      if (angleInRange) {
        intersectCount = 1;
      }

      // 2.7 根据交点数量确定偏移方向
      // 交点=0 → 内侧偏移（减小半径）；交点=1 → 外侧偏移（增大半径）
      isOutward = intersectCount === 1;
    }

    // 2.8 isReverse=true 时反转偏移方向（用户手动控制，与射线法默认方向叠加）
    if (isReverse) {
      isOutward = !isOutward;
    }

    // 3. 计算偏移后的半径
    const newRadius = isOutward
      ? radius + offsetDistance  // 外侧偏移：半径增大
      : radius - offsetDistance; // 内侧偏移：半径减小

    // 半径必须为正，偏移无效时返回 null，由调用方弹提示
    if (newRadius <= 0) {
      return null;
    }

    // 4. 计算偏移后的起止端点世界坐标
    // 起点：圆心 + 新半径 × (cos(startAngle), sin(startAngle))
    const localStartX = localCenter.x + newRadius * Math.cos(startAngle);
    const localStartY = localCenter.y + newRadius * Math.sin(startAngle);
    const worldStartPoint = referencePlane.localToWorld(new Vector2(localStartX, localStartY));

    // 终点：圆心 + 新半径 × (cos(endAngle), sin(endAngle))
    const localEndX = localCenter.x + newRadius * Math.cos(endAngle);
    const localEndY = localCenter.y + newRadius * Math.sin(endAngle);
    const worldEndPoint = referencePlane.localToWorld(new Vector2(localEndX, localEndY));

    // 5. 创建控制点并构造偏移后的 SketchArcObject
    // 圆心控制点必须显式创建并传入：SketchArcObject 对内建圆心默认 visible=false
    // （内建圆心仅服务于参数求解链路），显式传入后构造器会把圆心作为弧的子节点
    // 挂载且保持可见，与正常绘制圆弧（SketchArcDrawingHandler）的显示语义一致，
    // 保证等距圆弧的圆心在最终实体中可见。
    const centerControlPoint = new SketchPointObject({
      position: centerWorld.clone(),
      pointKind: SketchPointKind.ControlPoint,
      material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
      referencePlane,
      isAuxiliaryObject: false,
    });
    const startControlPoint = new SketchPointObject({
      position: worldStartPoint,
      pointKind: SketchPointKind.ControlPoint,
      material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
      referencePlane,
      isAuxiliaryObject: false,
    });
    const endControlPoint = new SketchPointObject({
      position: worldEndPoint,
      pointKind: SketchPointKind.ControlPoint,
      material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
      referencePlane,
      isAuxiliaryObject: false,
    });

    return new SketchArcObject({
      referencePlane,
      definition: {
        centerWorld: centerWorld.clone(),
        radius: newRadius,
        startAngle,
        endAngle,
        clockwise,
        segments,
      },
      material: LineMaterialFactory.getLineMaterialByBaseStyle(
        SketchEdgeBaseStyle.Basic,
        LineMaterialState.Default,
      ),
      startPoint: startControlPoint,
      endPoint: endControlPoint,
      centerPoint: centerControlPoint,
    });
  }

  /**
   * 求解抛物线默认偏移侧（射线法，与 SolidWorks 对齐）。
   *
   * 几何口径（与 SketchParabolaObject 采样点口径完全一致）：
   * P(t) = apex + side·t + axis·t²/(4f)，其中 axis = 顶点→焦点投影到参考面，
   * side = axis × 法线，f = 投影后焦距，t = 抛物线侧向参数（起止点参数的直接值）。
   * ① 计算起点→终点的弦方向，顺时针旋转90°得到方向A（chord × n）；
   * ② 从弦中点沿方向A发射射线，求解与抛物线的交点数量；
   * ③ 交点=0（方向A指向外侧）→ 默认内侧偏移（切向顺时针90°）；
   *    交点≥1（方向A指向内侧）→ 默认外侧偏移（切向逆时针90°）。
   *
   * 说明：等距实体创建路径与开口链方向统一（CombineSketchEntitiesUnifiedDirection）
   * 共用本方法，保证「抛物线单独偏移」与「抛物线在组合链中的默认偏移侧」判定一致。
   *
   * @param parabolaObject 源抛物线对象。
   * @param referencePlane 参考平面。
   * @returns 默认外侧标志；弦退化（首尾重合）无法判定时返回 null（调用方回退外侧）。
   */
  static resolveParabolaDefaultIsOutward(
    parabolaObject: SketchParabolaObject,
    referencePlane: ReferencePlane,
  ): boolean | null {
    // 1. 获取抛物线定义
    const definition = parabolaObject.getParabolaDefinition();
    const { focalWorld, apexWorld, startParameter, endParameter } = definition;

    // 2. 构建抛物线局部框架（axisDir = 顶点→焦点，sideDir = 垂直方向）
    const normal = referencePlane.normal.clone().normalize();
    const axisVector = focalWorld.clone().sub(apexWorld);
    // 将焦点-顶点向量投影到参考平面上（消除法线分量）
    axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
    const focalLength = axisVector.length();

    // 焦点与顶点重合时无法构建框架，偏移无意义
    if (focalLength < 1e-12) {
      return null;
    }

    const axisDir = axisVector.normalize();
    const sideDir = new Vector3().crossVectors(axisDir, normal).normalize();

    // 3. 计算起点、终点世界坐标（与 SketchParabolaObject 同一参数口径）
    // 抛物线参数方程：P(t) = apex + side·t + axis·t²/(4f)
    const startPointWorld = apexWorld.clone()
      .addScaledVector(sideDir, startParameter)
      .addScaledVector(axisDir, (startParameter * startParameter) / (4 * focalLength));
    const endPointWorld = apexWorld.clone()
      .addScaledVector(sideDir, endParameter)
      .addScaledVector(axisDir, (endParameter * endParameter) / (4 * focalLength));

    // 4. 计算弦方向与方向A（与线段偏移一致：chord × n = 顺时针90°）
    const chord = new Vector3().subVectors(endPointWorld, startPointWorld);
    const chordLength = chord.length();

    // 弦长为零（起点/终点重合）：无法判断方向，返回 null 交由调用方回退
    if (chordLength < 1e-12) {
      return null;
    }

    const chordDir = chord.normalize();
    // 方向A（世界坐标）：弦方向顺时针旋转90°
    const dirA = new Vector3().crossVectors(chordDir, normal).normalize();

    // 5. 弦中点作为射线起点
    const midPoint = new Vector3().addVectors(startPointWorld, endPointWorld).multiplyScalar(0.5);

    // 6. 将射线起点与方向A投影到抛物线局部2D坐标（axisDir, sideDir）
    const midToApex = new Vector3().subVectors(midPoint, apexWorld);
    const rayOriginLocal = new Vector2(midToApex.dot(axisDir), midToApex.dot(sideDir));
    const rayDirLocal = new Vector2(dirA.dot(axisDir), dirA.dot(sideDir));

    // 7. 射线与抛物线求交
    // 局部2D中抛物线参数方程：x = t²/(4f)，y = t（t 为抛物线侧向参数）
    // 射线参数方程：P = O + s·D（s ≥ 0）
    // 由 y 方程得 s = (t - Oy)/Dy，代入 x 方程消元得关于 t 的二次方程：
    // t² - (4f·Dx/Dy)·t + (4f·Dx·Oy/Dy - 4f·Ox) = 0
    let intersectCount = 0;
    const tMin = Math.min(startParameter, endParameter);
    const tMax = Math.max(startParameter, endParameter);
    const PARABOLA_EPS = 1e-9;

    if (Math.abs(rayDirLocal.y) > 1e-12) {
      // 一般情况（Dy ≠ 0）：二次方程求根
      const f = focalLength;
      const ox = rayOriginLocal.x;
      const oy = rayOriginLocal.y;
      const dx = rayDirLocal.x;
      const dy = rayDirLocal.y;

      const aCoeff = 1;
      const bCoeff = -4 * f * dx / dy;
      const cCoeff = 4 * f * (dx * oy / dy - ox);
      const discriminant = bCoeff * bCoeff - 4 * aCoeff * cCoeff;

      if (discriminant >= 0) {
        const sqrtD = Math.sqrt(Math.max(0, discriminant));
        // 依次检查两个根：交点必须位于射线正方向（s ≥ 0）且在抛物线参数范围内
        const tRoots = [
          (-bCoeff - sqrtD) / (2 * aCoeff),
          (-bCoeff + sqrtD) / (2 * aCoeff),
        ];
        for (const t of tRoots) {
          const s = (t - oy) / dy;
          if (s >= -PARABOLA_EPS && t >= tMin - PARABOLA_EPS && t <= tMax + PARABOLA_EPS) {
            intersectCount++;
          }
        }
      }
    } else {
      // 退化情况（Dy ≈ 0）：射线平行于 axis 轴，抛物线侧向参数 t = Oy
      const t = rayOriginLocal.y;
      const xOnParabola = (t * t) / (4 * focalLength);
      if (
        t >= tMin - PARABOLA_EPS
        && t <= tMax + PARABOLA_EPS
        && (xOnParabola - rayOriginLocal.x) * rayDirLocal.x >= -PARABOLA_EPS
      ) {
        intersectCount++;
      }
    }

    // 8. 根据交点数量确定默认偏移侧
    // 交点=0（方向A指向外侧）→ 默认内侧（false）；
    // 交点≥1（方向A指向内侧，穿过抛物线）→ 默认外侧（true）
    return intersectCount > 0;
  }

  /**
   * 抛物线偏移计算（方案A：采样法向偏移，输出样条）。
   * 
   * 说明：使用射线法自动判断默认偏移方向——
   * ① 计算起点→终点的弦方向，顺时针旋转90°得到方向A（chord × n）；
   * ② 从弦中点（起点→终点连线的中心点）沿方向A发射射线，求解与抛物线的交点数量；
   * ③ 交点=0（方向A指向外侧）→ 内侧偏移；
   *    交点≥1（方向A指向内侧）→ 外侧偏移；
   * ④ isReverse=true 时，将上述默认方向反转。
   * 
   * 几何口径：抛物线整体平移无法让两端点同时沿各自法向偏移（两端点法向不同，
   * 平移向量与端点切线的垂直关系一般不能同时成立），故改为对采样点列执行
   * miter 法向偏移并输出 SketchSplineObject——开放曲线端点由服务做纯法向平移，
   * 保证顶端加盖直线与端点切线垂直；偏移后的样条也严格经过法向等距点列。
   * 
   * 端点切向口径：抛物线端点切向由定义解析推导（dP/dt = side + axis·t/(2f)），
   * 不再依赖首/末采样段差分——差分近似会使顶点处偏移端点偏离真实法向位移
   * （如顶点也是端点时，与相连线段的偏移端点不重合，链延伸求交失败）。
   * 
   * @param parabolaObject 源抛物线对象。
   * @param distance 偏移距离（单位：毫米）。
   * @param isReverse false=使用射线法算出的默认方向；true=将默认方向反转。
   * @param referencePlane 参考平面。
   * @returns 偏移后的 SketchSplineObject 实例；定义无效或偏移自相交时返回 null。
   */
  static isometricSketchParabolaObject(
    parabolaObject: SketchParabolaObject,
    distance: number,
    isReverse: boolean,
    referencePlane: ReferencePlane
  ): SketchSplineObject | null {
    // 1. 获取抛物线定义与内置采样点列（点列沿参数区间从起点到终点）
    const definition = parabolaObject.getParabolaDefinition();
    const { focalWorld, apexWorld, startParameter, endParameter } = definition;
    const curvePoints = parabolaObject.getCurvePoints();
    if (curvePoints.length < 2) {
      return null;
    }

    // 2. 构建抛物线局部框架（axisDir = 顶点→焦点，sideDir = 垂直方向）
    const normal = referencePlane.normal.clone().normalize();
    const axisVector = focalWorld.clone().sub(apexWorld);
    // 将焦点-顶点向量投影到参考平面上（消除法线分量）
    axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
    const focalLength = axisVector.length();

    // 焦点与顶点重合时无法构建框架，偏移无意义
    if (focalLength < 1e-12) {
      return null;
    }

    const axisDir = axisVector.normalize();
    const sideDir = new Vector3().crossVectors(axisDir, normal).normalize();

    // ═══════════════════════════════════════════════════════════
    // 3. 射线法判断偏移方向（与开口链方向统一共用同一判定，见
    //    resolveParabolaDefaultIsOutward；弦退化时回退默认外侧）
    // ═══════════════════════════════════════════════════════════
    const defaultOutward = SketchEntityEquidistantOffset.resolveParabolaDefaultIsOutward(
      parabolaObject,
      referencePlane,
    );
    let isOutward = defaultOutward ?? true;

    // 3.1 isReverse=true 时反转偏移方向（用户手动控制，与射线法默认方向叠加）
    if (isReverse) {
      isOutward = !isOutward;
    }

    // ═══════════════════════════════════════════════════════════
    // 4. 解析端点切向（局部 2D）：抛物线端点切向 = side + axis·t/(2f)
    //    —— 起点/终点切向均取「采样行进方向」：起点 = 采样行进方向、终点同样沿
    //    采样行进方向（即服务端点差分口径 current - neighbor 的解析等价），
    //    与 SplineOffsetGeometryService 端点法向平移的切向口径一致。
    // ═══════════════════════════════════════════════════════════
    const parameterSpan = endParameter - startParameter;
    // 采样点列恒从起点参数走向终点参数（与参数区间正反无关）
    const samplingSign = parameterSpan >= 0 ? 1 : -1;

    // 将世界方向转为参考面局部 2D 单位方向（以 origin 为基点，保持相对方向）
    const toLocalUnitTangent = (originWorld: Vector3, tangentWorld: Vector3): Vector2 => {
      const originLocal = referencePlane.worldToLocal(originWorld.clone());
      const tipLocal = referencePlane.worldToLocal(originWorld.clone().add(tangentWorld));
      const localDir = new Vector2(tipLocal.x - originLocal.x, tipLocal.y - originLocal.y);
      const length = localDir.length();
      return length > 1e-12 ? localDir.multiplyScalar(1 / length) : new Vector2(1, 0);
    };

    // 起点/终点解析切向（dP/dt，指向参数增大方向），按采样行进方向定号
    const startTangentWorld = sideDir.clone()
      .addScaledVector(axisDir, startParameter / (2 * focalLength));
    const endTangentWorld = sideDir.clone()
      .addScaledVector(axisDir, endParameter / (2 * focalLength));

    const endpointTangents: SplineOffsetEndpointTangents = {
      start: toLocalUnitTangent(curvePoints[0]!, startTangentWorld.clone().multiplyScalar(samplingSign)),
      end: toLocalUnitTangent(curvePoints[curvePoints.length - 1]!, endTangentWorld.clone().multiplyScalar(samplingSign)),
    };

    // 5. 方向映射并复用样条偏移几何服务执行 miter 法向偏移
    //    射线法方向A = 弦顺时针90°（与服务 isReverse=false 同向）：
    //    - 交点≥1（方向A指向内侧）→ 外侧偏移 = 切向逆时针90° → 服务 isReverse=true；
    //    - 交点=0（方向A指向外侧）→ 内侧偏移 = 切向顺时针90° → 服务 isReverse=false。
    //    SplineOffsetGeometryService 对开放曲线端点做纯法向平移（基于端点切向差分，
    //    抛物线路径传入解析切向），保证「源端点 → 偏移端点」连线垂直于端点切线，
    //    顶端加盖直线连接点处垂直；距离的单位转换（毫米→米）由服务内部完成。
    const offsetGeometry = new SplineOffsetGeometryService().computeOffset(
      curvePoints,
      referencePlane,
      distance,
      isOutward,
      SketchEntityEquidistantOffset.PARABOLA_OFFSET_SPLINE_CONTROL_POINT_COUNT,
      endpointTangents,
    );
    if (!offsetGeometry) {
      // 偏移距离过大导致采样点列自相交（"打结"）或点数不足
      return null;
    }

    // 6. 构造并返回偏移后的 SketchSplineObject（与样条偏移共用同一构造口径）
    return this.buildOffsetSplineFromGeometry(offsetGeometry, referencePlane);
  }

  /**
   * 椭圆弧/椭圆 偏移计算。
   * 
   * 椭圆弧:
   * 说明：使用射线法自动判断默认偏移方向——
   * ① 计算起点→终点的弦方向，顺时针旋转90°得到方向A；
   * ② 从弦中点（起点→终点连线的中心点）沿方向A发射射线，求解与椭圆弧的交点数量；
   * ③ 交点=0（方向A指向外侧）→ 内侧偏移，减小长短半轴；
   *    交点=1（方向A指向内侧）→ 外侧偏移，增大长短半轴；
   * ④ isReverse=true 时，将上述默认方向反转。
   * 
   * 椭圆:
   * ① 默认偏移方向为外侧
   * ② isReverse=true 时，将上述默认方向反转。
   * 
   * 完整椭圆与椭圆弧分别处理控制点创建逻辑，直接构造偏移后的 SketchEllipseObject。
   *
   * 椭圆弧端点口径：端点取「源端点法向射线与偏移椭圆的交点」（法向 = 椭圆隐式
   * 方程梯度方向），再反解新椭圆参数角作为起止角——位移方向是法向（封盖直线与
   * 端点切线垂直），且端点严格落在偏移椭圆上（曲线末端与封盖不脱开），
   * 与 SolidWorks 顶端加盖（直线/圆弧）在连接点处垂直且相连的行为对齐。
   *
   * @param ellipseObject 源椭圆对象。
   * @param distance 偏移距离（单位：毫米）。
   * @param isReverse false=使用射线法算出的默认方向；true=将默认方向反转。
   * @param referencePlane 参考平面。
   * @returns 偏移后的 SketchEllipseObject 实例；半轴≤0 时返回 null。
   */
  static isometricSketchEllipticalArc(
    ellipseObject: SketchEllipseObject,
    distance: number,
    isReverse: boolean,
    referencePlane: ReferencePlane
  ): SketchEllipseObject | null {
    // 1. 获取椭圆定义：圆心、长短半轴、旋转角、起始角、终止角、是否顺时针、采样段数
    const definition = ellipseObject.getEllipseDefinition();
    const { centerWorld, radiusX, radiusY, rotation, startAngle, endAngle, clockwise, segments = 64 } = definition;

    // 单位转换：毫米 → 米
    const offsetDistance = distance * 0.001;

    // ═══════════════════════════════════════════════════════════
    // 2. 射线法判断偏移方向
    // ═══════════════════════════════════════════════════════════

    // 2.1 将椭圆中心投影到参考平面局部2D坐标
    const localCenter = referencePlane.worldToLocal(centerWorld.clone());

    // 2.2 在局部2D中计算起点和终点位置（基于原始长短半轴，
    //     先在未旋转椭圆上按参数角计算，再绕中心旋转 rotation）
    const localStartPoint = new Vector2(
      localCenter.x + radiusX * Math.cos(startAngle),
      localCenter.y + radiusY * Math.sin(startAngle),
    ).rotateAround(localCenter, rotation);
    const localEndPoint = new Vector2(
      localCenter.x + radiusX * Math.cos(endAngle),
      localCenter.y + radiusY * Math.sin(endAngle),
    ).rotateAround(localCenter, rotation);

    // 2.3 计算起点→终点的弦方向
    const chordDir = new Vector2().subVectors(localEndPoint, localStartPoint);
    const chordLength = chordDir.length();

    // 弦长为零（完整椭圆或起点/终点重合）：无法判断方向，默认外侧偏移
    let isOutward: boolean;
    if (chordLength < 1e-12) {
      isOutward = true; // 默认外侧偏移
    } else {
      chordDir.normalize();

      // 2.4 顺时针旋转90°得到方向A：(x, y) → (y, -x)
      // 数学原理：二维旋转矩阵 R_{-90°} = [[0, 1], [-1, 0]]
      const clockwiseDir = new Vector2(chordDir.y, -chordDir.x);

      // 2.5 将射线起点（弦中点）与方向变换到未旋转椭圆坐标系（绕中心旋转 -rotation）
      // 逆旋转公式：x' = x·cos(rotation) + y·sin(rotation)，
      //             y' = -x·sin(rotation) + y·cos(rotation)
      const cosR = Math.cos(rotation);
      const sinR = Math.sin(rotation);
      // 弦中点作为射线起点（与圆弧/抛物线实现保持一致）
      const midPoint = new Vector2().addVectors(localStartPoint, localEndPoint).multiplyScalar(0.5);
      const relStartX = midPoint.x - localCenter.x;
      const relStartY = midPoint.y - localCenter.y;
      // 未旋转系中的射线起点（相对椭圆中心）
      const rayStartX = relStartX * cosR + relStartY * sinR;
      const rayStartY = -relStartX * sinR + relStartY * cosR;
      // 未旋转系中的射线方向
      const rayDirX = clockwiseDir.x * cosR + clockwiseDir.y * sinR;
      const rayDirY = -clockwiseDir.x * sinR + clockwiseDir.y * cosR;

      // 2.6 射线与椭圆求交：椭圆方程 (x/a)² + (y/b)² = 1，射线 P(t) = O + t·D
      // 代入展开得二次方程 A·t² + B·t + C = 0：
      // A = (Dx/a)² + (Dy/b)²
      // B = 2·(Ox·Dx/a² + Oy·Dy/b²)
      // C = (Ox/a)² + (Oy/b)² - 1（弦中点一般不在椭圆上，C 为任意值）
      const coeffA = (rayDirX / radiusX) * (rayDirX / radiusX) + (rayDirY / radiusY) * (rayDirY / radiusY);
      const coeffB = 2 * (rayStartX * rayDirX / (radiusX * radiusX) + rayStartY * rayDirY / (radiusY * radiusY));
      const coeffC = (rayStartX / radiusX) * (rayStartX / radiusX) + (rayStartY / radiusY) * (rayStartY / radiusY) - 1;

      // 判别式：Δ < 0 说明射线与椭圆无交点（方向A指向外侧）
      const discriminant = coeffB * coeffB - 4 * coeffA * coeffC;

      let intersectCount = 0;

      if (discriminant >= 0) {
        // 求根后取位于射线正方向（t > 0）的最小根作为穿出点
        const sqrtD = Math.sqrt(discriminant);
        const t1 = (-coeffB - sqrtD) / (2 * coeffA);
        const t2 = (-coeffB + sqrtD) / (2 * coeffA);
        // 取严格大于容差的最小正根（排除起点本身）
        const ROOT_EPS = 1e-9;
        let tIntersect = Number.POSITIVE_INFINITY;
        if (t1 > ROOT_EPS) tIntersect = Math.min(tIntersect, t1);
        if (t2 > ROOT_EPS) tIntersect = Math.min(tIntersect, t2);

        if (Number.isFinite(tIntersect)) {
          // 未旋转系中的交点坐标（相对椭圆中心）
          const intersectX = rayStartX + tIntersect * rayDirX;
          const intersectY = rayStartY + tIntersect * rayDirY;
          // 由交点反求椭圆参数角：x = a·cosθ，y = b·sinθ → θ = atan2(y/b, x/a)
          const intersectAngle = Math.atan2(intersectY / radiusY, intersectX / radiusX);

          // 2.7 判断该交点参数角是否在 [startAngle, endAngle] 范围内
          // 角度归一化到 [0, 2π) 的辅助计算（内联实现）
          const TWO_PI = 2 * Math.PI;
          const normAngle = ((intersectAngle % TWO_PI) + TWO_PI) % TWO_PI;
          const normStart = ((startAngle % TWO_PI) + TWO_PI) % TWO_PI;
          const normEnd = ((endAngle % TWO_PI) + TWO_PI) % TWO_PI;

          // 完整椭圆弧（起点≈终点）：角度始终在范围内
          const isFullArc = Math.abs(normStart - normEnd) < 1e-9;

          let angleInRange: boolean;
          if (isFullArc) {
            angleInRange = true;
          } else if (clockwise) {
            // 顺时针：角度从 startAngle 减小到 endAngle
            if (normStart >= normEnd) {
              // 正常顺时针，不跨越0，例如 300°→60°（从大到小）
              angleInRange = normAngle <= normStart && normAngle >= normEnd;
            } else {
              // 跨越 0/2π，例如 60°→300°（顺时针经过0）
              angleInRange = normAngle <= normStart || normAngle >= normEnd;
            }
          } else {
            // 逆时针：角度从 startAngle 增大到 endAngle
            if (normStart <= normEnd) {
              // 正常逆时针，不跨越0，例如 60°→300°（从小到大）
              angleInRange = normAngle >= normStart && normAngle <= normEnd;
            } else {
              // 跨越 0/2π，例如 300°→60°（逆时针经过0）
              angleInRange = normAngle >= normStart || normAngle <= normEnd;
            }
          }

          if (angleInRange) {
            intersectCount = 1;
          }
        }
      }

      // 2.8 根据交点数量确定偏移方向
      // 交点=0 → 内侧偏移（减小半径）；交点=1 → 外侧偏移（增大半径）
      isOutward = intersectCount === 1;
    }

    // 2.9 isReverse=true 时反转偏移方向（用户手动控制，与射线法默认方向叠加）
    if (isReverse) {
      isOutward = !isOutward;
    }

    // 3. 计算偏移后的长短半轴
    const newRadiusX = isOutward
      ? radiusX + offsetDistance // 外侧偏移：半径增大
      : radiusX - offsetDistance; // 内侧偏移：半径减小
    const newRadiusY = isOutward
      ? radiusY + offsetDistance // 外侧偏移：半径增大
      : radiusY - offsetDistance; // 内侧偏移：半径减小

    // 半径必须为正，偏移无效时返回 null，由调用方弹提示
    if (newRadiusX <= 0 || newRadiusY <= 0) {
      return null;
    }

    // 4. 判断是否为完整椭圆（startAngle≈0 且 endAngle≈2π）
    const isFullEllipse = Math.abs(endAngle - startAngle - 2 * Math.PI) < 1e-6;

    if (isFullEllipse) {
      // 完整椭圆：新架构下 startPoint/endPoint 必填，需显式创建控制点
      // （起点=终点=角度 0 处，即长轴正向端点，与局部坐标旋转后的世界坐标对齐）

      // 起点/终点：按参数角在局部坐标系计算，再绕局部圆心旋转 rotation 后映射回世界坐标
      const computeFullEllipseEndpointWorld = (angle: number): Vector3 => {
        const localPoint = new Vector2(
          localCenter.x + newRadiusX * Math.cos(angle),
          localCenter.y + newRadiusY * Math.sin(angle),
        ).rotateAround(localCenter, rotation);
        return referencePlane.localToWorld(localPoint);
      };

      const fullEllipseStartPoint = new SketchPointObject({
        position: computeFullEllipseEndpointWorld(0),
        pointKind: SketchPointKind.ControlPoint,
        material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
        referencePlane,
        isAuxiliaryObject: false,
      });

      const fullEllipseEndPoint = new SketchPointObject({
        position: computeFullEllipseEndpointWorld(2 * Math.PI),
        pointKind: SketchPointKind.ControlPoint,
        material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
        referencePlane,
        isAuxiliaryObject: false,
      });

      const fullEllipseCenterPoint = new SketchPointObject({
        position: centerWorld.clone(),
        pointKind: SketchPointKind.ControlPoint,
        material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
        referencePlane,
        isAuxiliaryObject: false,
      });

      return SketchEntityEquidistantOffset.buildOffsetEllipseWithCardinalControlPoints(
        referencePlane,
        {
          centerWorld: centerWorld.clone(),
          radiusX: newRadiusX,
          radiusY: newRadiusY,
          rotation,
          startAngle: 0,
          endAngle: 2 * Math.PI,
          clockwise: false,
          segments: 64,
        },
        fullEllipseStartPoint,
        fullEllipseEndPoint,
        fullEllipseCenterPoint,
      );
    }

    // 椭圆弧：端点沿源端点法向平移 |d|，再反解新椭圆参数角。
    // 这样「源端点 → 偏移端点」连线垂直于源端点切线，顶端加盖直线连接点处垂直；
    // 若仍按同参数角取点，端点位移方向是参数径向 (cosθ, sinθ)，
    // 与真实法向 (cosθ/rx, sinθ/ry) 不平行，封盖连接点处不垂直。
    const offsetSign = isOutward ? 1 : -1;
    const resolveOffsetEndpointByNormal = (angle: number): { localPoint: Vector2; parameterAngle: number } => {
      // 未旋转系源端点（相对椭圆中心）
      const sourceX = radiusX * Math.cos(angle);
      const sourceY = radiusY * Math.sin(angle);
      // 椭圆隐式方程 x²/rx² + y²/ry² = 1 的梯度方向即法向：
      // (x/rx², y/ry²) ∝ (cosθ/rx, sinθ/ry)，归一化得到单位法向
      const normalX = Math.cos(angle) / radiusX;
      const normalY = Math.sin(angle) / radiusY;
      const normalLength = Math.hypot(normalX, normalY);
      // 射线方向：外侧沿法向、内侧沿反法向
      const dirX = offsetSign * normalX / normalLength;
      const dirY = offsetSign * normalY / normalLength;

      // 端点取「法向射线与偏移椭圆的交点」P + t·D 满足 (x/rx')² + (y/ry')² = 1：
      // 1. 交点严格位于偏移椭圆上——曲线采样末端与端点控制点重合，
      //    顶端加盖（直线/圆弧）在等距实体一侧不会脱开；
      // 2. 位移方向仍是法向——封盖与端点切线的垂直性保持。
      // 若直接取距离 d 的法向平移点（精确等距线上的点），该点偏离偏移椭圆，
      // 偏移距离相对半轴越大缝隙越明显（拖动后约束回贴只是事后修复）。
      const coeffA = (dirX / newRadiusX) * (dirX / newRadiusX)
        + (dirY / newRadiusY) * (dirY / newRadiusY);
      const coeffB = 2 * (
        sourceX * dirX / (newRadiusX * newRadiusX)
        + sourceY * dirY / (newRadiusY * newRadiusY)
      );
      const coeffC = (sourceX / newRadiusX) * (sourceX / newRadiusX)
        + (sourceY / newRadiusY) * (sourceY / newRadiusY) - 1;
      const discriminant = coeffB * coeffB - 4 * coeffA * coeffC;

      // 外侧偏移：源端点在偏移椭圆内部（根一正一负，取正根）；
      // 内侧偏移：源端点在偏移椭圆外部且射线指向内部（两根皆正，取较小者 = 近侧边界）。
      // 数值退化时回退到固定距离 d 的法向平移点，保证永远有合法端点。
      const sqrtD = Math.sqrt(Math.max(0, discriminant));
      const rootA = (-coeffB - sqrtD) / (2 * coeffA);
      const rootB = (-coeffB + sqrtD) / (2 * coeffA);
      const positiveRoots = [rootA, rootB].filter((t) => t > 1e-12);
      const intersectT = positiveRoots.length > 0
        ? Math.min(...positiveRoots)
        : offsetDistance;

      const offsetX = sourceX + intersectT * dirX;
      const offsetY = sourceY + intersectT * dirY;
      // 反解新椭圆上的参数角：x = rx'·cosθ'，y = ry'·sinθ' → θ' = atan2(y/ry', x/rx')
      const parameterAngle = Math.atan2(offsetY / newRadiusY, offsetX / newRadiusX);
      // 绕局部圆心旋转 rotation 后得到参考面局部 2D 坐标
      const localPoint = new Vector2(
        localCenter.x + offsetX,
        localCenter.y + offsetY,
      ).rotateAround(localCenter, rotation);
      return { localPoint, parameterAngle };
    };

    const startResolved = resolveOffsetEndpointByNormal(startAngle);
    const endResolved = resolveOffsetEndpointByNormal(endAngle);

    // 起止参数角保持源扫掠方向（与 THREE.EllipseCurve 起止角语义一致）：
    // 逆时针要求 start ≤ end、顺时针要求 start ≥ end；反解角规范化到 [0, 2π) 后
    // 按方向对终点角做 ±2π 调整（满椭圆走上面独立分支，不会进入这里）
    const FULL_TURN = 2 * Math.PI;
    const normalizeParameterAngle = (angle: number): number =>
      ((angle % FULL_TURN) + FULL_TURN) % FULL_TURN;
    const newStartAngle = normalizeParameterAngle(startResolved.parameterAngle);
    let newEndAngle = normalizeParameterAngle(endResolved.parameterAngle);
    if (clockwise) {
      // 顺时针：终点角必须不大于起点角
      if (newEndAngle > newStartAngle) {
        newEndAngle -= FULL_TURN;
      }
    } else {
      // 逆时针：终点角必须不小于起点角
      if (newEndAngle < newStartAngle) {
        newEndAngle += FULL_TURN;
      }
    }

    const worldStartPoint = referencePlane.localToWorld(startResolved.localPoint);
    const worldEndPoint = referencePlane.localToWorld(endResolved.localPoint);

    // 椭圆中心控制点（使用 ControlPoint 材质）
    const ellipseCenterControlPoint = new SketchPointObject({
      position: centerWorld.clone(),
      pointKind: SketchPointKind.ControlPoint,
      material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
      referencePlane,
      isAuxiliaryObject: false,
    });

    const startControlPoint = new SketchPointObject({
      position: worldStartPoint,
      pointKind: SketchPointKind.ControlPoint,
      material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
      referencePlane,
      isAuxiliaryObject: false,
    });

    const endControlPoint = new SketchPointObject({
      position: worldEndPoint,
      pointKind: SketchPointKind.ControlPoint,
      material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
      referencePlane,
      isAuxiliaryObject: false,
    });

    return SketchEntityEquidistantOffset.buildOffsetEllipseWithCardinalControlPoints(
      referencePlane,
      {
        centerWorld: centerWorld.clone(),
        radiusX: newRadiusX,
        radiusY: newRadiusY,
        rotation,
        startAngle: newStartAngle,
        endAngle: newEndAngle,
        clockwise,
        segments,
      },
      startControlPoint,
      endControlPoint,
      ellipseCenterControlPoint,
    );
  }

  /**
   * 构造等距椭圆并注册基准轴控制点。
   *
   * 说明：
   * 1. 正常绘制椭圆（SketchEllipseDrawingHandler）会通过 SketchEllipseControlPointFactory
   *    注册圆心与四个长短轴标记点：写入 ellipseControlPointRole、进入
   *    cardinalControlPoints、注册 connectedEdges；
   * 2. 该注册是参数求解编译椭圆结构方程（轴垂直/对称中点/点在椭圆上）与
   *    静默刷新按基准轴反推定义的前提；此前等距椭圆缺注册，拖源椭圆圆心时
   *    等距椭圆无任何结构方程、写回后也无法重建定义，导致其不联动；
   * 3. 等距椭圆复用与正常绘制完全相同的工厂注册口径，保证求解、拖拽、
   *    刷新与持久化语义一致。
   *
   * @param referencePlane 参考平面。
   * @param definition 等距椭圆定义。
   * @param startPoint 起点控制点。
   * @param endPoint 终点控制点。
   * @param centerPoint 圆心控制点。
   * @returns 完成基准轴控制点注册的等距椭圆。
   */
  private static buildOffsetEllipseWithCardinalControlPoints(
    referencePlane: ReferencePlane,
    definition: SketchEllipseDefinition,
    startPoint: SketchPointObject,
    endPoint: SketchPointObject,
    centerPoint: SketchPointObject,
  ): SketchEllipseObject {
    // 先构造等距椭圆本体（圆心/起止点沿用调用方显式创建的控制点）
    const ellipse = new SketchEllipseObject({
      referencePlane,
      definition,
      startPoint,
      endPoint,
      centerPoint,
      material: LineMaterialFactory.getLineMaterialByBaseStyle(
        SketchEdgeBaseStyle.Basic,
        LineMaterialState.Default,
      ),
    });

    // 再按正常绘制同一口径创建并注册四个长短轴标记点与圆心角色：
    // registerCardinalControlPoints 内部会把圆心替换注册为 Cardinal Center
    // （同一实例）并注册 connectedEdges，四个轴标记点注册为象限点角色。
    const controlPointFactory = new SketchEllipseControlPointFactory(referencePlane);
    const axisControlPoints = controlPointFactory.createAxisControlPoints({
      centerWorld: definition.centerWorld,
      radiusX: definition.radiusX,
      radiusY: definition.radiusY,
      rotation: definition.rotation,
    });
    controlPointFactory.registerCardinalControlPoints(ellipse, {
      centerPoint,
      axisControlPoints,
    });

    return ellipse;
  }

  /**
   * 样条曲线偏移计算（通用样条 & 样式样条）。
   * 
   * 说明：
   * 1. 将曲线采样点投影到参考面局部 2D 坐标；
   * 2. 使用 miter 法向偏移算法逐点偏移；
   * 3. 自相交检测——偏移距离过大导致曲线"打结"时返回 null；
   * 4. 从偏移后的曲线点列中按原控制点数量均匀采样选取新控制点；
   * 5. 直接构造并返回 SketchSplineObject。
   * 
   * @param splineObject 源样条对象（SketchSplineObject 或 SketchStyleSplineObject）。
   * @param distance 偏移距离（单位：毫米）。
   * @param isReverse false=正向，true=反向。
   * @param referencePlane 参考平面。
   * @returns 偏移后的 SketchSplineObject 实例；自相交或无法偏移时返回 null。
   */
  static isometricSketchSplineCurve(
    splineObject: SketchSplineObject | SketchStyleSplineObject,
    distance: number,
    isReverse: boolean,
    referencePlane: ReferencePlane
  ): SketchSplineObject | null {
    // 1. 获取采样曲线点
    const curvePoints = splineObject.getCurvePoints();
    if (curvePoints.length < 2) {
      return null;
    }

    // 2. 复用样条偏移几何服务执行 miter 偏移 + 自相交检测 + 控制点选取
    //    （创建路径与派生同步路径共用同一几何口径）
    const offsetGeometry = new SplineOffsetGeometryService().computeOffset(
      curvePoints,
      referencePlane,
      distance,
      isReverse,
      Math.max(2, splineObject.getControlPoints().length),
    );
    if (!offsetGeometry) {
      return null;
    }

    // 3. 构造并返回偏移后的 SketchSplineObject（与抛物线偏移共用同一构造口径）
    return this.buildOffsetSplineFromGeometry(offsetGeometry, referencePlane);
  }

  /**
   * 由样条偏移几何结果构造偏移样条实体。
   *
   * 说明：样条偏移（{@link isometricSketchSplineCurve}）与抛物线偏移
   * （{@link isometricSketchParabolaObject}）的产样条步骤完全一致，
   * 统一在此收口：
   * 1. 偏移点列映射回世界 3D 坐标；
   * 2. 按选取索引创建控制点对象（首尾为端点，闭合曲线终点独立实例）；
   * 3. 继承插值样条语义（curveMetadata = Spline + Open/Closed）；
   * 4. 构造 SketchSplineObject。
   *
   * @param offsetGeometry 样条偏移几何结果（局部 2D 点列 + 控制点索引 + 闭合标记）。
   * @param referencePlane 参考平面。
   * @returns 偏移后的 SketchSplineObject 实例。
   */
  private static buildOffsetSplineFromGeometry(
    offsetGeometry: SplineOffsetGeometryResult,
    referencePlane: ReferencePlane
  ): SketchSplineObject {
    const { offsetLocalPoints, controlPointIndices, isClosed } = offsetGeometry;

    // 1. 映射回世界 3D 坐标
    const worldOffsetPoints = offsetLocalPoints.map((point) =>
      referencePlane.localToWorld(point),
    );
    const pointCount = worldOffsetPoints.length;

    // 2. 创建所有控制点对象
    const allControlPoints = controlPointIndices.map((idx) =>
      new SketchPointObject({
        position: worldOffsetPoints[idx]!.clone(),
        pointKind: SketchPointKind.ControlPoint,
        material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
        referencePlane,
        isAuxiliaryObject: false,
      }),
    );

    const startPoint = allControlPoints[0]!;
    const endPoint = isClosed
      // 闭合样条：终点位置与起点相同，但需要独立实例
      ? new SketchPointObject({
        position: worldOffsetPoints[pointCount - 1]!.clone(),
        pointKind: SketchPointKind.ControlPoint,
        material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
        referencePlane,
        isAuxiliaryObject: false,
      })
      : allControlPoints[allControlPoints.length - 1]!;
    const interiorControlPoints = allControlPoints.slice(1, isClosed ? undefined : -1);

    // 3. 继承插值样条语义
    const curveMetadata = {
      curveKind: SketchBezierCurveKind.Spline,
      closureKind: isClosed
        ? SketchBezierCurveClosureKind.Closed
        : SketchBezierCurveClosureKind.Open,
    };

    // 4. 构造并返回偏移后的 SketchSplineObject
    return new SketchSplineObject({
      referencePlane,
      startPoint,
      endPoint,
      controlPoints: interiorControlPoints,
      curvePoints: worldOffsetPoints,
      curveMetadata,
    });
  }

  /**
   * 圆偏移计算。
   * 
   * 说明：修改半径实现偏移（向外增大半径，向内减小半径），
   * 圆心保持不变，SketchCircleObject 内部自动创建控制点。
   * 
   * @param circleObject 源圆对象。
   * @param distance 偏移距离（单位：毫米）。
   * @param isReverse false=外侧偏移，true=内侧偏移。
   * @param referencePlane 参考平面。
   * @returns 偏移后的 SketchCircleObject 实例；半径≤0 时返回 null。
   */
  static isometricSketchCircleObject(
    circleObject: SketchCircleObject,
    distance: number,
    isReverse: boolean,
    referencePlane: ReferencePlane
  ): SketchCircleObject | null {
    // 1. 提取圆心和半径
    const center = circleObject.vec3dCenter;
    const radius = circleObject.radius;

    // 2. 确认方向并计算新半径
    const offsetMeters = distance * 0.001; // mm 转换为 m
    let newRadius: number;
    if (isReverse) {
      // 内侧
      newRadius = radius - offsetMeters;
    } else {
      // 外侧
      newRadius = radius + offsetMeters;
    }

    if (newRadius <= 0) {
      return null;
    }

    // 3. 构造并返回偏移后的 SketchCircleObject
    // SketchCircleObject 内部会自动创建圆心控制点和象限点
    return new SketchCircleObject({
      referencePlane,
      center,
      radius: newRadius,
      material: LineMaterialFactory.getLineMaterialByBaseStyle(
        SketchEdgeBaseStyle.Basic,
        LineMaterialState.Default,
      ),
    });
  }

  // ═══════════════════════════════════════════════════════════
  // 样条偏移的 miter/自相交/控制点选取几何已抽取到 SplineOffsetGeometryService，
  // 创建路径（isometricSketchSplineCurve）与派生同步路径（CurveOffsetDerivedSyncService）
  // 共用同一几何口径。
  // ═══════════════════════════════════════════════════════════

}

export { SketchEntityEquidistantOffset };
